import { createSelector } from '@reduxjs/toolkit';
import { selectAllIssues } from '@ui/features/quote-builder/state/selectors/issue';
import { buildElementId } from '@ui/features/quote-builder/state/utilities/element-id';
import { RootState } from '@ui/store';

import { getIssues, getQuoteLineOrThrow } from '../utilities/selector-guards';

export const selectQuoteLine = (state: RootState, quoteLineUuid: string) =>
  getQuoteLineOrThrow(state.quoteBuilder, quoteLineUuid);

export const selectQuoteLineLeadTimeIssues = createSelector(
  [selectAllIssues, (_: RootState, quoteLineUuid: string) => quoteLineUuid],
  (issues, quoteLineUuid) =>
    getIssues(issues, {
      area: 'quoteLine',
      field: 'leadTime',
      quoteLineUuid,
    }),
);

export const selectQuoteLineLeadTimeNotesIssues = createSelector(
  [selectAllIssues, (_: RootState, quoteLineUuid: string) => quoteLineUuid],
  (issues, quoteLineUuid) =>
    getIssues(issues, {
      area: 'quoteLine',
      field: 'leadTimeNotes',
      quoteLineUuid,
    }),
);

export const selectQuoteLineWithLeadTimeIssues = createSelector(
  [selectQuoteLine, selectQuoteLineLeadTimeIssues],
  (quoteLine, leadTimeIssues) => ({
    ...quoteLine,
    leadTimeIssues,
  }),
);

export const selectQuoteLineDetails = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).customerPartId,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).lastWin,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).leadTime,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).leadTimeNotes,
    (_, quoteLineUuid: string) => quoteLineUuid,
    selectQuoteLineLeadTimeIssues,
    selectQuoteLineLeadTimeNotesIssues,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).partInsights,
  ],
  (
    customerPartId,
    lastWin,
    leadTime,
    leadTimeNotes,
    quoteLineUuid,
    leadTimeIssues,
    leadTimeNotesIssues,
    partInsights,
  ) => {
    const leadTimeElementId = buildElementId({
      uuid: quoteLineUuid,
      field: 'leadTime',
    });

    return {
      customerPartId,
      lastWin,
      leadTime,
      leadTimeNotes,
      leadTimeIssues,
      leadTimeNotesIssues,
      leadTimeElementId,
      partInsights,
    };
  },
);

export const selectQuoteLineSpecs = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).specs,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).partId,
  ],
  (specs, partId) => {
    return { specs, partId };
  },
);

export const selectQuoteLineDocumentsAction = createSelector(
  [
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).documentIds,
    (state: RootState, quoteLineUuid: string) =>
      selectQuoteLine(state, quoteLineUuid).partId,
  ],
  (documentIds, partId) => {
    return { documentIds, partId };
  },
);
